import _ from 'lodash'
import {
  assign,
  cloneDeep,
  endsWith,
  find,
  flow,
  get,
  getOr,
  includes,
  isArray,
  isEmpty,
  isEqual,
  isNil,
  keyBy,
  map,
  negate,
  omit,
  startsWith,
} from 'lodash/fp'
import { buffers, delay, eventChannel } from 'redux-saga'
import {
  call,
  cancel,
  cancelled,
  fork,
  join,
  put,
  race,
  select,
  spawn,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects'

import { analytics } from 'analytics/analytics'
import {
  APPLICATION_FETCH_UPDATE_ERROR,
  APPLICATION_FETCH_UPDATE_SUCCESS,
  APPLICATION_INSTALL_UPDATE_ERROR,
  APPLICATION_INSTALL_UPDATE_SUCCESS,
  CLEAR_EKYC_RESULT,
  CLEAR_REMOTE_SELLING_RESULT,
  CONFIRM_REMOTE_SELLING,
  ENTER_EKYC_F2F,
  REMOTE_SELLING_CREATE_NEW_LINK,
  REMOTE_SELLING_RESET_DATA,
  REMOTE_SELLING_SUBMIT_RESUME_MODAL,
  REMOTE_SELLING_USING_EXISTS_LINK,
} from 'core/action-types'
import { applicationFetchUpdate, clearEkycResults, remoteSellingCreateNewLink } from 'core/actions'
import values from 'core/data-model/constants/values'
import { validateAdvisorPermission } from 'core/service/distributor/permission'
import { getServiceWrapper } from 'core/service-wrapper'
import { appDocsGet, appDocsPut } from 'core/service-wrapper/model/esub-app-doc'
import middlewareSvc from 'core/service/advisorzone-middleware/request-wrapper'

import { getAppConfig } from 'deploy-env/app-config'
import {
  addError,
  changeCurrentApp,
  cleanDCA,
  closeRemoteSellingResumeModal,
  deletePhoto,
  getAIUWStatus,
  hideAgeRevalidating,
  hideDialog,
  hideLoading,
  hideModalLoading,
  initOldRemoteSelling,
  initRemoteSelling,
  loadApplication,
  mergeApplication,
  openSendingLinkModalRemoteSelling,
  removeError,
  removeFile,
  resetIsAcknowledgeAgeChanged,
  reValidateAge,
  saveAppData,
  setAppStatusDrafted,
  setAppStatusSigned,
  setAppValue,
  setBackIdCardLaserCode,
  setFaceImage,
  setFaceRecognitionImage,
  setFrontIdCardImage,
  setFrontPassportImage,
  setOffOtp,
  setOpenIdentifyRespondingModal,
  setReplicationStatus,
  setResendEkycLink,
  showAgeRevalidating,
  showErrorDialog,
  showLoading,
  showModalLoading,
  showSubmittedFlashMessage,
  showSyncingDialog,
  updateSourceData,
  updateEnhancedAddressStatus,
} from 'e-submission/apps/actions'
import {
  getAIUW,
  getAllAttachment,
  getAppInformation,
  getAppList,
  getCurrentApp,
  getCurrentAppId,
  getData,
  getDraft,
  getRelationshipListForNewATP,
  getReplicationStatus,
  isHideDialog,
  isEnhancedAddress,
  isUpdateSourceData,
} from 'e-submission/apps/selectors'
import { optionsResumeRMS } from 'e-submission/containers/Dialog/constants'
import { APP_STATUS, REMOTE_SELLING, REPLICATION_STATUS } from 'e-submission/domain/data-model/constants'
import {
  getAppStatus,
  getPolicyId,
  getRemoteSellingCaseId,
  getRemoteSellingState,
  getSkippedInsuredEkyc,
  getSkippedPayerEkyc,
  havePayer,
  isAdditionalDocument,
  isCreditCardRelationshipDocument,
  isForeigner,
  isSelectedRemoteSelling,
  listSignature,
  isVerifyEkycCompleted,
  isNeedConfirmationLetterSelector,
} from 'e-submission/domain/data-model/form/selectors'
import { getAppByOptyId } from 'e-submission/domain/service/opty'
import {
  getATPDefaultRelation,
  getEkycResult,
  getTitleValue,
  shouldApplyEkycSourceData,
} from 'e-submission/sagas/utils'
import {
  base64ToBlob,
  blobToDataUrl,
  getVerificationDetail,
  getWithExpiry,
  isCordova,
  prepareEkycDetailResult,
  productDisableFlag,
  setWithExpiry,
} from 'e-submission/utils'
import { getIdentityUser } from 'identity/selectors'
import { getCachedSelfAdvisor } from 'identity/saga'
import { setProcessingSuccess } from 'quick-quote/actions'
import ROUTE_PATH from 'quick-quote/constants/path'
import { getToggles } from 'quick-quote/feature-toggles'
import { setNotificationMessage } from 'quick-quote/notification/actions'
import { updateOptyStageEAppCreate } from 'quick-quote/opty/actions'
import { getQueryParam } from 'quick-quote/opty/utils'
import { getAllowedAgentTypes } from 'quick-quote/selectors'

import { onListDocument } from './document'
import { onListPayment, reCheckQuickPayStatus } from './payment'
import { mappingCaseKeeper, resendCasePayload, setAttachmentPayload } from './policy-payload/ekyc-case-keeper'

export const MINIMUM_DELAY_SHOW_SUBMMIT_DIALOG = 5000
export const CHECK_SUBMITTING_DELAY = 10000
export const CHECK_SYNC_DELAY = 1000

export const DEBOUNCE_DELAY = 600
export const RETRY_DELAY = 0
export const REGEN_BI_TIMEOUT_MS = 60000

const isExcludeSave = (action) => {
  const fieldId = get('fieldId', action)

  if (fieldId === 'createdAt') {
    return true
  }

  if (fieldId === 'updatedAt') {
    return true
  }

  if (startsWith('receipts', fieldId) && !isEqual('receipts', fieldId)) {
    return true
  }

  if (startsWith('insured', fieldId) && !isEqual('insured', fieldId)) {
    return true
  }

  if (startsWith('beneficiary', fieldId) && !isEqual('beneficiary', fieldId)) {
    return true
  }

  if (startsWith('payer', fieldId) && !isEqual('payer', fieldId)) {
    return true
  }

  if (startsWith('FATCA', fieldId) && !isEqual('FATCA', fieldId)) {
    return true
  }

  if (startsWith('document.certifyByAgent', fieldId) && !isEqual('certifyByAgent', fieldId)) {
    return true
  }

  if (startsWith('KYC', fieldId) && !isEqual('KYC', fieldId)) {
    return true
  }

  if (startsWith('creditCardRelationship', fieldId) && !isEqual('creditCardRelationship', fieldId)) {
    return true
  }

  if (startsWith('insurancePrivacyConsent', fieldId) && isEqual('insurancePrivacyConsent', fieldId)) {
    return true
  }

  if (startsWith('marketingPrivacyConsent', fieldId) && isEqual('marketingPrivacyConsent', fieldId)) {
    return true
  }

  if (startsWith('DCA', fieldId) && !isEqual('DCA', fieldId)) {
    return true
  }
  if (startsWith('ATP', fieldId) && !isEqual('ATP', fieldId)) {
    return true
  }
  if (isEqual('remoteSelling.email', fieldId) || isEqual('remoteSelling.phoneNumber', fieldId)) {
    return true
  }

  if (
    startsWith('luckyNumber', fieldId) &&
    !isEqual('luckyNumber.status', fieldId) &&
    !isEqual('luckyNumber.selected', fieldId)
  ) {
    return true
  }

  return false
}
const isNoDebounceSave = (action) => {
  const fieldId = get('fieldId', action)

  if (startsWith('receipts', fieldId)) {
    return true
  }

  const noDebounceFields = ['_attachments', 'payment.type', 'policyId', 'receiptNo', 'status']
  return noDebounceFields.includes(fieldId)
}

const getUserId = get('profile.sub')
const isLoggedIn = flow(getUserId, negate(isNil))

export const canSync = function*() {
  const user = yield select(getIdentityUser)

  return isLoggedIn(user)
}

const removeNullValue = (obj) => {
  try {
    return JSON.parse(JSON.stringify(obj), (key, value) => {
      if (value === null) return undefined
      return value
    })
  } catch (err) {
    return obj
  }
}

export let taskSync = null
export const onSave = function*(service, currentApp) {
  const { getApplicationRev, saveApplication } = service

  const user = yield select(getIdentityUser)

  let app = currentApp

  if (isNil(currentApp)) {
    app = yield select(getCurrentApp)
  }

  if (yield call(canSync)) {
    while (true) {
      try {
        const _rev = yield call(getApplicationRev, app, user)
        const newApp = removeNullValue({
          ...app,
          _rev,
          updatedAt: Date.now(),
        })

        if (newApp.status !== 'submitted') {
          taskSync = yield call(saveApplication, newApp, user)
        }

        yield put({ type: 'SAVE_APPLICATION_SUCCESS' })

        break
      } catch (err) {
        if (get('response.status', err) !== 409) {
          yield put({ type: 'SAVE_APPLICATION_FAIL' })
          break
        }
      }

      yield delay(RETRY_DELAY)
    }
  }
}

export const onDebounceSave = function*(service, action) {
  if (isExcludeSave(action)) {
    const fieldId = get('fieldId', action)

    if (getToggles().ENABLE_EDA && (isEqual('ATP.firstName', fieldId) || isEqual('ATP.lastName', fieldId))) {
      const app = yield select(getCurrentApp)
      const relationType = getATPDefaultRelation(app)
      const isManualATPRelationship = get('ATP.isManualRelationship', app)
      if (!isManualATPRelationship) {
        const relationshipList = yield select(getRelationshipListForNewATP)
        const relationship = find(({ value }) => value === relationType, relationshipList)

        yield put(setAppValue('ATP.relations', relationship))
      }
    }

    return
  }

  if (!isNoDebounceSave(action)) {
    yield delay(DEBOUNCE_DELAY)
  }

  yield fork(onSave, service)
}

export const onLoad = function*(service, isNewApp = false) {
  const { getApplication } = service

  if (yield call(canSync)) {
    const user = yield select(getIdentityUser)
    const app = yield select(getCurrentApp)

    const appId = get('_id', app)

    if (appId) {
      const data = yield select(getData)
      if (isEmpty(data)) {
        yield take('SET_DATA')
      }

      let response
      try {
        response = yield call(getApplication, app, user)
        if (response.remoteSelling) {
          response.remoteSelling = {
            ...response.remoteSelling,
            isInCall: false,
            callStatus: { isInCall: false },
            isModalCreatingRemoteSellingOpen: false,
            isResendModalOpen: false,
            isOpenNewRemoteSellingPreviewInfoModal: false,
          }
        }
      } catch (err) {}

      const newApp = removeNullValue(omit(['_rev'], response))

      // update wrapper for switch product handler
      const onLoadApp = _.get(getServiceWrapper(), 'e_submission.onLoadApp')
      if (onLoadApp) {
        yield call(onLoadApp, newApp, isNewApp)
      }
      yield put(loadApplication(newApp))
    }
  }
}

export const onCreate = function*(service, action) {
  yield put(resetIsAcknowledgeAgeChanged())
  console.log('onCreate reset ack')

  const { saveApplication, setAttachment, getApplicationRev } = service
  if (yield call(canSync)) {
    const user = yield select(getIdentityUser)
    const app = yield select(getCurrentApp)
    const serviceWrapper = getServiceWrapper()

    const appId = get('_id', app)
    if (appId) {
      try {
        yield put.resolve(showLoading())

        const biProps = yield select((state) => state.quickQuoteInterface.biProps)
        const bi = JSON.stringify(omit(['accessToken', 'basicPlan.image'], biProps))

        let data = new Blob([bi], { type: 'text/plain', encoding: 'UTF-8' })
        if (isCordova) {
          data = yield call(blobToDataUrl, data)
        }

        yield put(setAppValue('createdAt', Date.now()))
        yield put(setAppValue('updatedAt', Date.now()))

        const draft = yield select(getDraft)

        if (biProps !== undefined) {
          const productCategory = appDocsGet('productCategory', 'quickQuote.biProps')(biProps)
          let isTMORate = false
          if (productCategory === values.INVESTMENT) {
            isTMORate = yield call(serviceWrapper.products.INVESTMENT.isUsedCoiTMORate)
          } else if (productCategory === values.WHOLE_LIFE) {
            isTMORate = yield call(serviceWrapper.products.COMMON.isUsedTMORate)
          }
          yield call(saveApplication, { ...draft, calculatedFromTMORate: isTMORate }, user)
        } else {
          yield call(saveApplication, draft, user)
        }

        const _rev = yield call(getApplicationRev, app, user)
        const newApp = {
          ...app,
          _rev,
        }

        yield call(setAttachment, newApp, 'bi', data, user)

        yield [call(onLoad, service, true), take('SAVE_APPLICATION_SUCCESS')]

        yield put(updateOptyStageEAppCreate())
      } catch (err) {
        console.error(err)
        const message = err.message
        const notificationMessage = getToggles().ENABLE_RED_TRUCK_ERROR_MSG
          ? { type: 'GenericError', message: message }
          : { type: 'GenericError' }
        yield put(setNotificationMessage(notificationMessage))
        window.history.back()
      } finally {
        yield put(hideLoading())
        yield put(setProcessingSuccess())
      }
    }
  }
}

export const waitInitSubmitPage = function*(service) {
  const app = yield select(getCurrentApp)
  const appStatus = getAppStatus(app)
  if (appStatus === APP_STATUS.SIGNED) {
    yield call(reCheckQuickPayStatus, service, app)
    yield call(onListPayment, service)
    yield call(onListDocument, service)
  }
}

export const patchBrokenPolicy = function*(service) {
  const { getPolicyStatus } = service

  const app = yield select(getCurrentApp)
  const policyId = get('policyId', app)
  const appStatus = get('status', app)
  const user = yield select(getIdentityUser)

  // Patch broken policy that connection is failed during server response back when OTP.
  if (policyId && appStatus !== APP_STATUS.SIGNED && appStatus !== APP_STATUS.SUBMITTED) {
    try {
      const policy = yield call(getPolicyStatus, user, policyId)
      const policyStatus = get('status', policy)
      if (policyStatus === APP_STATUS.SIGNED) {
        yield [put(setOffOtp()), take('SAVE_APPLICATION_SUCCESS')]
        yield [put(setAppStatusSigned()), take('SAVE_APPLICATION_SUCCESS')]
      }
    } catch (err) {}
  }
}

export const resetNumberMoobanArkanForEnhancedAddress = function*(app) {
  const isChkEnhancedAddress = yield select(isEnhancedAddress)
  if (isChkEnhancedAddress) {
    const insuredCurrentAddress = _.get(app, 'insured.currentAddress')
    if (
      insuredCurrentAddress &&
      !_.has(insuredCurrentAddress, 'addressNo') &&
      _.has(insuredCurrentAddress, 'numberMoobanArkan')
    ) {
      console.log('reset currentAddress.numberMoobanArkan')
      yield put(setAppValue('insured.currentAddress.numberMoobanArkan', null))
      yield put(saveAppData())
    }

    const insuredRegisteredAddress = _.get(app, 'insured.registeredAddress')
    if (
      insuredRegisteredAddress &&
      insuredRegisteredAddress.notUseCurrentAddress &&
      !_.has(insuredRegisteredAddress, 'addressNo') &&
      _.has(insuredRegisteredAddress, 'numberMoobanArkan')
    ) {
      console.log('reset registeredAddress.numberMoobanArkan')
      yield put(setAppValue('insured.registeredAddress.numberMoobanArkan', null))
      yield put(saveAppData())
    }

    const insuredOfficeAddress = _.get(app, 'insured.officeAddress')
    if (
      insuredOfficeAddress &&
      !_.has(insuredOfficeAddress, 'isSameAsCurrentAddress') &&
      !_.has(insuredOfficeAddress, 'addressNo') &&
      _.has(insuredOfficeAddress, 'numberMoobanArkan')
    ) {
      console.log('reset officeAddress.numberMoobanArkan')
      yield put(setAppValue('insured.officeAddress.numberMoobanArkan', null))
      yield put(saveAppData())
    }

    const payer = _.get(app, 'payer')
    if (payer) {
      const payerAddress = _.get(payer, 'payerAddress')
      if (payerAddress && !_.has(payerAddress, 'addressNo') && _.has(payerAddress, 'numberMoobanArkan')) {
        console.log('reset payer.payerAddress.numberMoobanArkan')
        yield put(setAppValue('payer.payerAddress.numberMoobanArkan', null))
        yield put(saveAppData())
      }
    }

    const beneficiaryList = _.get(app, 'beneficiary', [])
    for (let index = 0; index < beneficiaryList.length; index++) {
      const beneficiary = beneficiaryList[index]
      const beneficiaryAddress = _.get(beneficiary, 'beneficiaryAddress')
      if (
        beneficiaryAddress &&
        !_.has(beneficiaryAddress, 'addressNo') &&
        _.has(beneficiaryAddress, 'numberMoobanArkan')
      ) {
        console.log(`reset beneficiary[${index}].beneficiaryAddress.numberMoobanArkan`)
        yield put(setAppValue(`beneficiary[${index}].beneficiaryAddress.numberMoobanArkan`, null))
        yield put(saveAppData())
      }
    }
  }
}

export const onApplicationCardClicked = function*(service, history, action) {
  const { getBasicPlanCode } = service
  yield put(changeCurrentApp(action.appId))
  yield take('LOAD_APP')
  const app = yield select(getCurrentApp)
  const status = _.get(app, 'status', null)
  const basicPlanCode = getWithExpiry('basicPlanCode') || []
  if (basicPlanCode.length === 0 && (status === APP_STATUS.SUBMITTED || status === APP_STATUS.SIGNED)) {
    const data = yield call(getBasicPlanCode)
    if (data instanceof Array) {
      setWithExpiry(
        'basicPlanCode',
        data.map((o) => o.planCode),
        3600000
      )
    }
  }

  if (![APP_STATUS.SIGNED, APP_STATUS.SUBMITTING, APP_STATUS.SUBMITTED].includes(status)) {
    yield call(resetNumberMoobanArkanForEnhancedAddress, app)
  }

  const checkedPremiumCalStatus = _.get(app, 'checkedPremiumCalStatus', null)
  if (status === APP_STATUS.SIGNED) {
    const hasReceiptPaid = _.get(app, 'receipts', []).some((r) => _.get(r, 'paid', false))
    const hasPayLater = _.get(app, 'submitWithoutPayment.selected.value', false)

    if (hasReceiptPaid || hasPayLater) {
      yield call(onNavigate, service, history)
    } else if (
      checkedPremiumCalStatus === 'N' ||
      checkedPremiumCalStatus === 'CPQ' ||
      checkedPremiumCalStatus === 'CPE'
    ) {
      yield call(onNavigate, service, history)
    } else {
      yield put(setAppValue('checkedPremiumCalStatus', null))
      yield put(saveAppData())
      yield put(reValidateAge(action.appId))
    }
  } else if (
    checkedPremiumCalStatus !== 'CPQ' &&
    checkedPremiumCalStatus !== 'CPE' &&
    status !== APP_STATUS.SUBMITTED &&
    status !== APP_STATUS.SUBMITTING &&
    checkedPremiumCalStatus !== 'N'
  ) {
    yield put(setAppValue('checkedPremiumCalStatus', null))
    yield put(saveAppData())
    yield put(reValidateAge(action.appId))
  } else if (checkedPremiumCalStatus !== 'N') {
    const remoteSelling = _.get(app, ['remoteSelling'], null)
    const selectedRemoteSelling = _.get(app, ['selectedRemoteSelling'], null)
    const isRemoteSelling = _.get(app, ['isRemoteSelling'], null)

    if (status === APP_STATUS.SUBMITTED) {
      history.push('form/submit')
    } else if (
      (isNil(status) || status === APP_STATUS.DRAFTED) &&
      isRemoteSelling &&
      !selectedRemoteSelling &&
      getToggles().ENABLE_APPMAN_REMOTE_SELLING
    ) {
      history.push('form/ekyc')
    } else if (isNil(status) || status === APP_STATUS.DRAFTED) {
      history.push('form')
    }
  }
}

export const onUpdate = function*(service, action) {
  yield put.resolve(showLoading())
  yield put(resetIsAcknowledgeAgeChanged())
  const app = yield select(getCurrentApp)
  const appId = get('_id', app)

  try {
    if (yield call(canSync)) {
      if (appId) {
        try {
          yield call(onLoad, service, false)
          const status = getOr('drafted', 'status', app)
          if (isNil(status) || status === APP_STATUS.DRAFTED) {
            const currentApp = yield select(getCurrentApp)
            const remoteSelling = getOr(false, 'isRemoteSelling', currentApp)
            const selectedRemoteSelling = getOr(false, 'selectedRemoteSelling', currentApp)
            const isAllowedRevisedRemoteSelling = getToggles().ENABLE_APPMAN_REMOTE_SELLING

            if (remoteSelling && isAllowedRevisedRemoteSelling) {
              yield put(initRemoteSelling())
            }

            if (selectedRemoteSelling && !isAllowedRevisedRemoteSelling) {
              yield put(initOldRemoteSelling())
            }
          }

          yield call(waitInitSubmitPage, service)
          yield call(patchBrokenPolicy, service)
        } catch (err) {
        } finally {
          yield put(hideLoading())
        }
      }
    }
  } catch (e) {
    console.error('onUpdate :', e.message)
  }
}

const isListPage = () => {
  const pathname = window.location.href
  return includes('/e-submission/list', pathname)
}

export const onSync = function*(service) {
  if (isCordova) {
    const { subscribeReplicationStatus, listApplicationSummary } = service

    const channel = eventChannel((emit) => {
      subscribeReplicationStatus(emit, (err) => {
        console.error(err)
      })
      return () => subscribeReplicationStatus()
    }, buffers.sliding(1))

    while (true) {
      if (yield cancelled()) {
        channel.close()
        break
      }

      const newStatus = yield take(channel)
      const currentStatus = yield select(getReplicationStatus)

      if (currentStatus === newStatus && currentStatus === REPLICATION_STATUS.IDLE) continue

      yield put(setReplicationStatus(newStatus))

      if (isListPage()) {
        const user = yield select(getIdentityUser)

        if (isLoggedIn(user)) {
          try {
            const response = yield call(listApplicationSummary, user)
            const appSummaries = flow(map(omit(['_rev'])), keyBy('_id'))(response)

            if (isListPage()) {
              const currentAppId = yield select(getCurrentAppId)
              yield put(mergeApplication(currentAppId, appSummaries))
            }
          } catch (err) {}
        }
      }
    }
  }
}

export const onList = function*(service) {
  const { listApplicationSummary } = service
  if (getToggles().ENABLE_MANUAL_CORDOVA_UPDATE && window.chcp) {
    // start update
    yield put(applicationFetchUpdate())
    // wait for update procrss to completed
    const { fetchError } = yield race({
      fetchSuccess: take(APPLICATION_FETCH_UPDATE_SUCCESS),
      fetchError: take(APPLICATION_FETCH_UPDATE_ERROR),
    })
    // block go to esub if failed
    if (fetchError) {
      return
    }

    const { installError } = yield race({
      installSuccess: take(APPLICATION_INSTALL_UPDATE_SUCCESS),
      installError: take(APPLICATION_INSTALL_UPDATE_ERROR),
    })
    // block go to esub if failed
    if (installError) {
      return
    }
  }
  yield put.resolve(showLoading())

  const toggleMW = yield middlewareSvc.getRequest('config', { ignoreAccessToken: true })
  const statusEnhancedAddress = _.get(toggleMW, 'data.enableEnhancedAddress', true)
  yield put(updateEnhancedAddressStatus(statusEnhancedAddress))
  const statusUpdateSourceData = _.get(toggleMW, 'data.enableUpdateSourceData', false)
  yield put(updateSourceData(statusUpdateSourceData))

  let currentAppId = yield select(getCurrentAppId)
  // const currentAppId = yield select(getCurrentAppId)
  const user = yield select(getIdentityUser)

  yield put(resetIsAcknowledgeAgeChanged())

  if (isLoggedIn(user)) {
    yield put(mergeApplication(currentAppId, {}))
  } else {
    yield put(mergeApplication(null, {}))
    yield put(hideLoading())
    return
  }

  const search = getQueryParam(_.get(window.location, ['search'], null))
  const isDeeplink = localStorage.getItem('isDeeplink') === 'true'
  const deeplinkParam = isDeeplink
    ? JSON.parse(localStorage.getItem('deeplinkParam'))
    : getQueryParam(_.get(history, ['location', 'search'], ''))
  const deeplinkOptyId = _.get(deeplinkParam, ['opty'], null)

  try {
    let deeplinkData = {}
    let appSummaries = {}
    if (search && !_.isEmpty(search) && deeplinkOptyId) {
      const { response } = yield call(getAppByOptyId, user, deeplinkOptyId)
      currentAppId = _.get(response, ['applicationId'], null)
      deeplinkData = {
        [currentAppId]: {
          ...response,
        },
      }
      deeplinkData[currentAppId]._id = currentAppId
      appSummaries = flow(map(omit(['_rev'])), keyBy('_id'))(deeplinkData)
      yield put(mergeApplication(currentAppId, appSummaries))

      const responseDefault = yield call(listApplicationSummary, user)
      appSummaries = flow(map(omit(['_rev'])), keyBy('_id'))(responseDefault)
      // yield put(mergeApplication(currentAppId, appSummaries))
    } else {
      const responseDefault = yield call(listApplicationSummary, user)
      appSummaries = flow(map(omit(['_rev'])), keyBy('_id'))(responseDefault)
      yield put(mergeApplication(currentAppId, appSummaries))
    }
  } catch (err) {
  } finally {
    yield put(hideLoading())
  }
}

export const onValidateAdvisorPermission = function*() {
  const advisor = yield call(getCachedSelfAdvisor)
  const allowedAgentTypes = yield select(getAllowedAgentTypes)

  return validateAdvisorPermission(advisor, allowedAgentTypes)
}

export const onSubmit = function*(service) {
  try {
    yield put.resolve(showLoading())

    if (getToggles().ALLOW_CHECK_CREATE_POLICY_PERMISSION) {
      const isValidPermission = yield call(onValidateAdvisorPermission)

      if (!isValidPermission) {
        yield put(showErrorDialog('invalidPermission'))
        throw new Error('Invalid permission')
      }
    }

    const app = yield select(getCurrentApp)
    const policyId = getPolicyId(app)

    let action = []
    if (isNil(policyId)) {
      action = yield [
        race({
          succeed: take('CREATE_POLICY_SUCCEED'),
          failed: take('CREATE_POLICY_FAILED'),
        }),
        put({ type: 'CREATE_POLICY' }),
      ]
      console.info('Creating policy')
    } else {
      action = yield [
        race({
          succeed: take('UPDATE_POLICY_SUCCEED'),
          failed: take('UPDATE_POLICY_FAILED'),
        }),
        put({ type: 'UPDATE_POLICY' }),
      ]
    }

    const [{ succeed, failed }] = action

    if (failed) {
      throw new Error(failed.message)
    }

    if (isNil(policyId)) {
      const { policyID } = succeed

      yield [put(setAppValue('policyId', policyID)), take('SAVE_APPLICATION_SUCCESS')]
    }

    const signatures = listSignature(app)

    yield map(({ key }) => put(removeFile(key)), signatures)
  } catch (err) {
    console.error(err)
    window.history.back()
  } finally {
    console.info('onSubmit competed')
    yield put(hideLoading())
  }
}

export const fetchAllDocument = function*(service) {
  const { listDocument } = service

  const user = yield select(getIdentityUser)
  const app = yield select(getCurrentApp)
  const policyId = getPolicyId(app)
  if (!policyId) return []
  const allDocuments = yield call(listDocument, user, policyId)

  return flow(
    map(({ documentType, documentId, description, code, isInsured }) => {
      if (isAdditionalDocument(documentType)) {
        return {
          name: `${documentType}_${documentId}`,
          text: description,
          isInsured,
        }
      }
      if (isCreditCardRelationshipDocument(documentType)) {
        return {
          name: `${documentType}_${documentId}`,
          text: description,
        }
      }
      return {
        name: code,
      }
    })
  )(allDocuments)
}

export const onValidationSubmit = function*() {
  const validateActions = [cleanDCA]
  for (const action of validateActions) {
    yield put(action())
  }
}

export const onSend = function*(service, action) {
  const history = action.payload
  try {
    const app = yield select(getCurrentApp)
    const delayTask = yield fork(delay, MINIMUM_DELAY_SHOW_SUBMMIT_DIALOG)
    const appWithId = yield select(getAppInformation)
    const productDisabled = productDisableFlag(appWithId)
    yield [put(setAppValue('productDisabled', productDisabled)), take('SAVE_APPLICATION_SUCCESS')]
    yield put(showSyncingDialog())

    yield call(onValidationSubmit)

    const documents = yield call(fetchAllDocument, service)

    const allAttachments = flow(assign({ documents }), getAllAttachment)(app)

    yield put(
      showSyncingDialog({
        step: 'uploading',
        attachments: allAttachments,
      })
    )

    yield [put(setAppValue('status', APP_STATUS.SUBMITTING)), take('SAVE_APPLICATION_SUCCESS')]

    if (yield select(isHideDialog)) {
      yield cancel()
    }

    yield put(
      showSyncingDialog({
        step: 'submitting',
        attachments: allAttachments,
      })
    )

    let count = 0,
      invalidMessage = false
    while (true) {
      if (yield select(isHideDialog)) {
        yield cancel()
        break
      }

      count = count + 1

      try {
        const [{ succeed, failed }] = yield [
          race({
            succeed: take('SUBMIT_POLICY_SUCCEED'),
            failed: take('SUBMIT_POLICY_FAILED'),
          }),
          put({ type: 'SUBMIT_POLICY' }),
        ]

        if (failed) {
          invalidMessage = failed.message
          throw new Error(failed.message)
        }

        if (succeed) {
          break
        }
      } catch (err) {
        yield delay(CHECK_SYNC_DELAY)
      } finally {
        if (isArray(invalidMessage)) {
          count = getAppConfig().MAXIMUM_RETRY_SUBMIT_COUNT
          yield put(
            showSyncingDialog({
              step: 'invalid-attachments',
              invalidAttachments: invalidMessage,
            })
          )
          break // NOSONAR
        }

        if (count >= getAppConfig().MAXIMUM_RETRY_SUBMIT_COUNT) {
          yield put(
            showSyncingDialog({
              step: 'failed',
              attachments: allAttachments,
            })
          )
          break // NOSONAR
        }
      }
    }

    yield join(delayTask)

    if (count < getAppConfig().MAXIMUM_RETRY_SUBMIT_COUNT) {
      history.replace('/e-submission/list')

      yield put(hideDialog())
    }
  } catch (err) {
    console.error(err)
    yield put(hideDialog())
  }
}

export const fetchApp = function*(service, app, payload) {
  const { getApplication } = service

  const user = yield select(getIdentityUser)
  const currentApp = yield call(getApplication, app, user)

  const newApp = {
    ...currentApp,
    ...payload,
  }

  yield [take('SAVE_APPLICATION_SUCCESS'), call(onSave, service, newApp)]
  yield put(loadApplication(newApp))
}
export const checkSubmitting = function*(service, app) {
  const { getPolicyStatus } = service

  const user = yield select(getIdentityUser)
  const policyId = getPolicyId(app)

  try {
    const response = yield call(getPolicyStatus, user, policyId)

    if (app.status === response.status) return

    let payload = {
      status: response.status,
    }

    if (response.status === APP_STATUS.FAILED) {
      const documents = yield call(fetchAllDocument, service)

      payload = {
        ...payload,
        documents,
      }
    }

    yield call(fetchApp, service, app, payload)

    if (response.status === APP_STATUS.SUBMITTED) {
      yield put(showSubmittedFlashMessage())
      analytics.sendCustomEvent({
        category: 'Submission_Event',
        action: 'Success_Submit_AG',
        label: '',
      })
    }
  } catch (err) {
    analytics.sendCustomEvent({
      category: 'Submission_Event',
      action: 'Fail_Submit_AG',
      label: '',
    })
    console.error(err)
  }
}
export const onCheckSubmitting = function*(service) {
  yield [take('MERGE_APP'), take('MERGE_APP')]

  while (true) {
    if (yield cancelled()) {
      break
    }

    const appList = yield select(getAppList)
    const submittingList = get(APP_STATUS.SUBMITTING, appList)

    const replicationStatus = yield select(getReplicationStatus)

    if (!isEmpty(submittingList) && replicationStatus === REPLICATION_STATUS.IDLE) {
      yield map((app) => call(checkSubmitting, service, app))(submittingList)
    }

    yield delay(CHECK_SUBMITTING_DELAY)
  }
}

export const onErrorOtpAttachments = function*(service, history, action) {
  const errors = get('payload.errors')(action)
  try {
    yield [put(setAppStatusDrafted()), take('SAVE_APPLICATION_SUCCESS')]

    //TODO should change to "for ( error of errors )" and " put(deletePhoto(get('documentType')(error))),"
    for (var index in errors) {
      yield [put(deletePhoto(get(`${index}.documentType`)(errors))), take('SAVE_APPLICATION_SUCCESS')]
    }
  } catch (e) {
  } finally {
    history.replace('/e-submission/form/signature')
  }
}

export const onCreateEkycByDevice = function*(service, action) {
  try {
    const _action = cloneDeep(action)
    _action.payload.createdFromDevice = true
    yield onSendLinkEkyc(service, _action)
  } catch (e) {
    console.error(`ERROR : onCreateEkycByDevice ${e.message}`)
  }
}

export const onSendLinkEkyc = function*(service, action) {
  const app = yield select(getCurrentApp)
  yield put.resolve(showLoading())
  const user = yield select(getIdentityUser)
  try {
    const { getVerifyCitizenId, getVerifyPassportNumber, createEkycCase, resendEkycLink } = service
    const currentProprietorId = _.get(app, `ekycInfo.${action.payload.insuredOrPayer}.proprietorId`, '')

    if (!action.payload.onDupModal && getToggles().ENABLE_REUSE_EKYC) {
      if (!currentProprietorId) {
        const citizenId = action.payload.citizenId
        const passportNumber = action.payload.passportNumber
        let response
        if (isEmpty(action.payload.citizenId)) {
          const resp = yield call(getVerifyPassportNumber, user, passportNumber)
          response = {
            ...resp.response,
          }
        } else {
          const resp = yield call(getVerifyCitizenId, user, citizenId)
          response = {
            ...resp.response,
          }
        }

        const cases = get(['data'], response)
        let verifiedCases

        if (isSelectedRemoteSelling(app)) {
          verifiedCases = cases.filter((caseElement, index) => {
            return (
              caseElement.verificationCache.status === 'verified' &&
              !_.isEmpty(caseElement.verificationCache.idFaceRecognitionResult)
            )
          })
        } else {
          verifiedCases = cases.filter((caseElement, index) => {
            return (
              caseElement.verificationCache.status === 'verified' &&
              _.isEmpty(caseElement.verificationCache.idFaceRecognitionResult)
            )
          })
        }

        const verifiedCasesAmount = verifiedCases.length
        const isEkycDuplicateCitizenId = verifiedCasesAmount > 0
        if (isEkycDuplicateCitizenId) {
          return yield put(
            addError('EKYC_DUPLICATED_CITIZENID', {
              message: 'duplicate citizenId',
              citizenId,
              count: verifiedCasesAmount,
              cases: verifiedCases,
            })
          )
        }
      } else {
        const resendPayload = resendCasePayload(app, currentProprietorId)
        return yield call(resendEkycLink, user, resendPayload)
      }
    }
    const ekycCaseKeeperPayload = mappingCaseKeeper(
      app,
      action.payload.insuredOrPayer,
      action.payload.createdFromDevice,
      _.get(app, ['selectedRemoteSelling'], false)
    )
    const ekycCase = yield call(createEkycCase, user, ekycCaseKeeperPayload)
    yield put(setResendEkycLink(action.payload.insuredOrPayer))
    const proprietorId = get(['response', 'proprietors', '0', 'id'], ekycCase)
    const caseId = get(['response', 'proprietors', '0', 'caseId'], ekycCase)
    const policyId = get(['response', 'policies', '0', 'id'], ekycCase)
    yield put(setAppValue(`ekycInfo.${action.payload.insuredOrPayer}.proprietorId`, proprietorId))
    yield put(setAppValue(`ekycInfo.${action.payload.insuredOrPayer}.caseId`, caseId))
    yield put(setAppValue(`ekycInfo.${action.payload.insuredOrPayer}.policyId`, policyId))
    yield put(setAppValue(`ekycInfo.${action.payload.insuredOrPayer}.resendLink`, true))
  } catch (error) {
    console.error(`ERROR onSendLinkEkyc : ${error.message}`)
    yield put(addError('EKYC_VERIFY_CITIZENID', error))
  } finally {
    yield put(hideLoading())
    if (action.payload.createdFromDevice) {
      yield call(onVerifyEkyc, service, action)
    }
  }
}

export const onVerifyEkyc = function*(service, action) {
  yield put(removeError('EKYC_VERIFY_PROPRIETORID'))
  yield put(removeError('EKYC_VERIFY_PROPRIETORID_PASSPORT'))
  const user = yield select(getIdentityUser)
  let app = yield select(getCurrentApp)
  let { insuredOrPayer } = action.payload
  let verificationId = null
  let isEkycVerifyProprietorIdError = null
  const proprietorId = _.get(app, `ekycInfo.${insuredOrPayer}.proprietorId`, '')
  const { verifyByProprietorId, saveAttachmentFromURL, reValidateAge } = service
  if (proprietorId) {
    try {
      let isSkipped = false
      switch (insuredOrPayer) {
        case 'insured':
          isSkipped = getSkippedInsuredEkyc(app)
          break
        case 'payer':
          isSkipped = getSkippedPayerEkyc(app)
          break
      }
      if (isSkipped) {
        return
      }
      yield put.resolve(showModalLoading())
      const { response } = yield call(verifyByProprietorId, user, proprietorId)
      const verificationDetail = getVerificationDetail(response)
      verificationId = _.get(response, 'id', null)
      yield put(setAppValue(`ekycInfo.${insuredOrPayer}.verificationDetail`, { ...verificationDetail }, true))
      yield put(setAppValue(`ekycInfo.${insuredOrPayer}.verificationId`, verificationId))
      const hasPayer = havePayer(app)
      const isNeedConfirmationLetter = isNeedConfirmationLetterSelector(insuredOrPayer, app)
      if (isNeedConfirmationLetter) {
        const passportResult = _.get(verificationDetail, ['passportResult'], null)
        if (!passportResult?.verified && !_.isEmpty(passportResult)) {
          yield put(addError('EKYC_VERIFY_PROPRIETORID_PASSPORT', 'Foreigner passportResult is null'))
          yield put(setOpenIdentifyRespondingModal(false, insuredOrPayer))
        }
      }

      const { frontIdCardResult, backIdCardResult } = prepareEkycDetailResult(response)
      yield put(setBackIdCardLaserCode(backIdCardResult.laser, insuredOrPayer))

      const verificationDetailEdit = _.get(verificationDetail, 'frontIdCardResult.edits')

      // check frontIdCardResult.edits is empty or not
      if (verificationDetailEdit) {
        if (getToggles().ENABLE_EKYC_F2F_RECREATE_CASE) {
          yield put(setAppValue(`ekycInfo.${insuredOrPayer}.isDataConfirmed`, true, true))
        } else {
          yield put(setAppValue('ekycInfo.isDataConfirmed', true, true))
        }
        // Set {insured | payer}.idNo.xxxxx to be the same as frontIdCardResult.edits.xxxxx
        // DIGSALTOOL-1991 - Not replacing EKYC data firstname, lastname, citizenID
        if (frontIdCardResult.citizenId) {
          yield put(
            setAppValue(`${insuredOrPayer}.idNo.citizenId`, frontIdCardResult.citizenId.replaceAll('-', ''), true)
          )
        }
      }
      yield take('SAVE_APPLICATION_SUCCESS')

      let url = _.get(verificationDetail, 'frontIdCardResult.response.idCardImageUrl', null)
      if (!url) {
        url = _.get(verificationDetail, 'passportResult.response.idCardImageUrl', null)
      }
      if (url) {
        let fileName = 'id-card'
        if (isForeigner(app, insuredOrPayer)) {
          fileName = 'passport'
        }
        const payload = setAttachmentPayload(insuredOrPayer, app, url, fileName)
        const newAttachments = yield call(saveAttachmentFromURL, user, payload)
        yield put(setAppValue(`_attachments.${payload.attachmentId}`, newAttachments[payload.attachmentId]))
        action.payload.imageName = insuredOrPayer === 'insured' ? `insured-${fileName}` : `owner-${fileName}`
        yield call(onGetAttachment, service, action)
      }

      // FACE recognition
      const frUrl = _.get(verificationDetail, 'idFaceRecognitionResult.response.idFaceRecognitionImageUrl', null)
      if (frUrl) {
        const payload = setAttachmentPayload(insuredOrPayer, app, frUrl, 'face-recognition')
        const newAttachments = yield call(saveAttachmentFromURL, user, payload)
        yield put(setAppValue(`_attachments.${payload.attachmentId}`, newAttachments[payload.attachmentId]))
        action.payload.imageName = insuredOrPayer === 'insured' ? 'insured-face-recognition' : 'owner-face-recognition'
        yield call(onGetAttachment, service, action)
      }
      const faceImageUrl = _.get(verificationDetail, 'idFaceRecognitionResult.response.faceImageUrl', null)
      if (faceImageUrl) {
        const payload = setAttachmentPayload(insuredOrPayer, app, faceImageUrl, 'face-image')
        const newAttachments = yield call(saveAttachmentFromURL, user, payload)
        yield put(setAppValue(`_attachments.${payload.attachmentId}`, newAttachments[payload.attachmentId]))
        action.payload.imageName = insuredOrPayer === 'insured' ? 'insured-face-image' : 'owner-face-image'
        yield call(onGetAttachment, service, action)
      }

      const confirmationLetterUrl = _.get(
        verificationDetail,
        'insurancePolicyAgreementResult.response.resultDocumentUrl',
        null
      )
      if (confirmationLetterUrl) {
        const appId = _.get(app, '_id')
        const reValidateAgeGuard = yield call(reValidateAge, user, appId)
        const isAgeChanged = _.get(reValidateAgeGuard, 'ageChange', false) ? 'Y' : 'N'
        if (isAgeChanged === 'Y') {
          yield put(showModalLoading())
        }

        // const policyId = yield select(getPolicyIdCurrentApp)
        const payload = setAttachmentPayload(
          insuredOrPayer,
          app,
          confirmationLetterUrl,
          'confirmation-letter',
          'application/pdf'
        )

        const newAttachments = yield call(saveAttachmentFromURL, user, payload)
        yield put(setAppValue(`_attachments.${payload.attachmentId}`, newAttachments[payload.attachmentId]))
      }

      yield put(removeError('EKYC_VERIFY_PROPRIETORID'))

      app = yield select(getCurrentApp)
      const dopaResultVerified = _.get(verificationDetail, 'dopaResult.verified', false)
      const applyEkycSourceData = shouldApplyEkycSourceData(insuredOrPayer, app)
      if (applyEkycSourceData) {
        const { title, firstName, lastName } = getEkycResult(_.get(app, 'ekycInfo', {}), insuredOrPayer)
        yield put(setAppValue(`${insuredOrPayer}.firstName`, firstName))
        yield put(setAppValue(`${insuredOrPayer}.lastName`, lastName))

        const existingTitleValue = _.get(app, `${insuredOrPayer}.title.value`, '')
        if (title && existingTitleValue !== 'other') {
          const titleValue = getTitleValue(title)
          yield put(setAppValue(`${insuredOrPayer}.title.value`, titleValue))
          yield put(setAppValue(`${insuredOrPayer}.title.text`, title))
        }

        yield put(setAppValue(`${insuredOrPayer}.eKYCsource`, true))
        yield put(saveAppData())
      }

      const insurancePolicyAgreementResultVerified = _.get(
        verificationDetail,
        'insurancePolicyAgreementResult.verified',
        false
      )
      const idFaceRecognitionResultVerified = _.get(verificationDetail, 'idFaceRecognitionResult.verified', false)
      let verifyEkycCompleted = isVerifyEkycCompleted(
        app,
        isNeedConfirmationLetter,
        dopaResultVerified,
        idFaceRecognitionResultVerified,
        insurancePolicyAgreementResultVerified
      )

      const isCompareDOBeKYC = _.get(app, 'isCompareDOBeKYC', 'N')
      const oldCheckedPremiumCalStatus = _.get(app, 'checkedPremiumCalStatus', 'NP')

      if (insuredOrPayer === 'insured' && verifyEkycCompleted && isCompareDOBeKYC !== 'Y') {
        const user = yield select(getIdentityUser)
        const appId = _.get(app, '_id')
        const reValidateAgeResp = yield call(reValidateAge, user, appId)
        const ageChange = _.get(reValidateAgeResp, 'ageChange', false) ? 'Y' : 'N'
        yield put(setAppValue('reValidateAgeData', reValidateAgeResp))
        yield put(setAppValue('ageChange', ageChange))
        yield put(saveAppData())

        const isEkycDobDiffFromQqDob = _.get(reValidateAgeResp, 'isEkycDobDiffFromQqDob')
        const isSpecificAgeChanged = _.get(reValidateAgeResp, 'isSpecificAgeChanged', false)
        let shouldRevalidateAge = false

        if (isEkycDobDiffFromQqDob && !isSpecificAgeChanged) {
          shouldRevalidateAge = true
        }

        if (isSpecificAgeChanged) {
          shouldRevalidateAge = false
          yield put(setAppValue('checkedPremiumCalStatus', 'N'))
        }

        yield put(setAppValue('isCompareDOBeKYC', 'Y'))
        yield put(saveAppData())

        if (shouldRevalidateAge && oldCheckedPremiumCalStatus !== 'N') {
          yield take('SAVE_APPLICATION_SUCCESS')
          yield put(setAppValue('checkedPremiumCalStatus', null))
          yield put(saveAppData())
          yield put({ type: 'RE_VALIDATE_AGE', payload: { appId } })
        } else {
          yield put.resolve(hideModalLoading())
        }
      } else {
        yield put.resolve(hideModalLoading())
      }
    } catch (error) {
      console.log('GIFT!!!!! onVerifyEkyc error', { error })
      yield put.resolve(hideModalLoading())
      yield put(addError('EKYC_VERIFY_PROPRIETORID', error))
      isEkycVerifyProprietorIdError = true
      if (
        action.payload.createdFromDevice &&
        action.payload.openRespondModalOnFailedCase &&
        typeof action.payload.openRespondModalOnFailedCase === 'function'
      ) {
        action.payload.openRespondModalOnFailedCase()
      }
    } finally {
      console.log('GIFT!!!!! onVerifyEkyc finally', { isEkycVerifyProprietorIdError })
      if (action.payload.createdFromDevice && !isEkycVerifyProprietorIdError) {
        console.log('GIFT!!!!! onVerifyEkyc finally if', { action })
        const ekycConfiguration = {
          tenantConfigClientName: getAppConfig().EKYCSDK_TENANT_CONFIG_CLIENT_NAME,
          baseURL: getAppConfig().EKYCSDK_BASE_URL,
          tenantConfigURL: getAppConfig().EKYCSDK_TENANT_CONFIG_URL,
          ekycURL: getAppConfig().EKYCSDK_EKYC_URL,
          verificationId,
        }
        yield window.ekycCallEvent('open', ekycConfiguration)
        const _action = cloneDeep(action)
        _action.payload.createdFromDevice = false
        yield onVerifyEkyc(service, _action)
      }
    }
  }
}

export const onGetAttachment = function*(service, action) {
  const app = yield select(getCurrentApp)
  const user = yield select(getIdentityUser)
  const imageName = action.payload.imageName
  const insuredOrPayer = action.payload.insuredOrPayer
  const { getAttachment } = service
  try {
    const response = yield call(getAttachment, app, imageName, user)
    const defaultType = { type: 'image/jpeg' }
    const type = _.get(action, 'payload.type', defaultType)
    const dataUrl = yield call(blobToDataUrl, new Blob([response], type))
    if (endsWith('face-recognition', imageName)) {
      yield put(setFaceRecognitionImage(dataUrl, insuredOrPayer))
    } else if (endsWith('face-image', imageName)) {
      yield put(setFaceImage(dataUrl, insuredOrPayer))
    } else {
      yield put(setFrontIdCardImage(dataUrl, insuredOrPayer))
      yield put(setFrontPassportImage(dataUrl, insuredOrPayer))
    }
    return response
  } catch (error) {
    return
  }
}

export const onCreateNewLinkRemoteSelling = function*(service, action) {
  const app = yield select(getCurrentApp)

  const remoteSelling = get('remoteSelling', app)

  const resettingRemoteSelling = {
    ...remoteSelling,
    isLinkModalOpen: true,
    isResendModalOpen: false,
  }
  yield put(setAppValue('remoteSelling', resettingRemoteSelling))
}

export const onResetRemoteSelling = function*(service, action) {
  const app = yield select(getCurrentApp)
  const remoteSellingFields = REMOTE_SELLING.initialValue
  const insuredEmail = get('insured.email', app)
  const insuredPhoneNumber = get('insured.mobileNumber', app)

  const resettingRemoteSelling = {
    ...remoteSellingFields,
    email: insuredEmail,
    phoneNumber: insuredPhoneNumber,
    sms: insuredPhoneNumber,
    isDataConfirmed: true,
    isOpenNewRemoteSellingPreviewInfoModal: false,
  }
  yield put(setAppValue('remoteSelling', resettingRemoteSelling))
}

export const onReuseEkyc = function*(service, action) {
  yield put.resolve(showLoading())
  const user = yield select(getIdentityUser)
  const app = yield select(getCurrentApp)

  const { proprietorId, insuredOrPayer } = action.payload
  const { verifyByProprietorId, saveAttachmentFromURL, createEkycCase, updateVerificationRef } = service
  try {
    const { response } = yield call(verifyByProprietorId, user, proprietorId)
    const verificationRef = get(['id'], response)
    yield put(setAppValue(`ekycInfo.${insuredOrPayer}.verificationRef`, verificationRef))

    const isReuse = true
    const selectedRemoteSelling = _.get(app, ['selectedRemoteSelling'], false)
    const ekycCaseKeeperPayloadWithOutNotifyType = mappingCaseKeeper(
      app,
      insuredOrPayer,
      isReuse,
      selectedRemoteSelling,
      verificationRef
    )

    const newEkycCase = yield call(createEkycCase, user, ekycCaseKeeperPayloadWithOutNotifyType)
    const newProprietorId = get(['response', 'proprietors', '0', 'id'], newEkycCase)
    const caseId = get(['response', 'proprietors', '0', 'caseId'], newEkycCase)
    const policyId = get(['response', 'policies', '0', 'id'], newEkycCase)
    const hasPayer = havePayer(app)
    const isNeedConfirmationLetter = isNeedConfirmationLetterSelector(insuredOrPayer, app)

    if (!selectedRemoteSelling || !isNeedConfirmationLetter) {
      yield call(updateVerificationRef, user, newProprietorId, {
        verificationRef: verificationRef,
      })
      yield put(setOpenIdentifyRespondingModal(false, insuredOrPayer))
    }

    yield put(setAppValue(`ekycInfo.${insuredOrPayer}.proprietorId`, newProprietorId, true))
    yield put(setAppValue(`ekycInfo.${insuredOrPayer}.caseId`, caseId))
    yield put(setAppValue(`ekycInfo.${insuredOrPayer}.policyId`, policyId))
    yield put(setAppValue(`ekycInfo.${insuredOrPayer}.resendLink`, true, true))

    const newVerifyDetail = yield call(verifyByProprietorId, user, newProprietorId)
    const verificationDetail = getVerificationDetail(newVerifyDetail.response)

    yield put(setAppValue(`ekycInfo.${insuredOrPayer}.verificationDetail`, { ...verificationDetail }, true))

    // save attachment
    const url = _.get(verificationDetail, 'frontIdCardResult.response.idCardImageUrl', null)
    if (url) {
      const payload = setAttachmentPayload(insuredOrPayer, app, url, 'id-card')
      const newAttachments = yield call(saveAttachmentFromURL, user, payload)
      yield put(setAppValue(`_attachments.${payload.attachmentId}`, newAttachments[payload.attachmentId]))

      action.payload.imageName = insuredOrPayer === 'insured' ? 'insured-id-card' : 'owner-id-card'
      yield call(onGetAttachment, service, action)
    }

    const { frontIdCardResult, backIdCardResult } = prepareEkycDetailResult(response)
    yield put(setBackIdCardLaserCode(backIdCardResult.laser, insuredOrPayer))

    const verificationDetailEdit = _.get(verificationDetail, 'frontIdCardResult.edits')

    // check frontIdCardResult.edits is empty or not
    if (verificationDetailEdit) {
      if (getToggles().ENABLE_EKYC_F2F_RECREATE_CASE) {
        yield put(setAppValue(`ekycInfo.${insuredOrPayer}.isDataConfirmed`, true, true))
      } else {
        yield put(setAppValue('ekycInfo.isDataConfirmed', true, true))
      }
      // Set {insured | payer}.idNo.xxxxx to be the same as frontIdCardResult.edits.xxxxx
      // DIGSALTOOL-1991 - Not replacing EKYC data firstname, lastname, citizenID.
      if (!frontIdCardResult.citizenId) return
      yield put(setAppValue(`${insuredOrPayer}.idNo.citizenId`, frontIdCardResult.citizenId.replaceAll('-', ''), true))
    }

    yield take('SAVE_APPLICATION_SUCCESS')
  } catch (error) {
    yield put(addError('EKYC_REUSE_CASE', error))
    return
  } finally {
    yield put(hideLoading())
    yield call(onVerifyEkyc, service, action)
  }
}

export const onSubmitRemoteSellingResumeModal = function*(service, action) {
  const app = yield select(getCurrentApp)
  const remoteSellingResumeState = get('remoteSelling.resumeState', app)
  const remoteSellingCaseId = getRemoteSellingCaseId(app)
  let { sectionName } = action.payload

  let caseId = null
  let nextProcess = 'ekyc'
  let resume = false

  if (remoteSellingCaseId) {
    caseId = remoteSellingCaseId
  }

  if (sectionName === 'videoConsent') {
    resume = true
    nextProcess = 'none'
  }

  if (remoteSellingResumeState === optionsResumeRMS[2].value) {
    yield put(remoteSellingCreateNewLink())
  } else if (remoteSellingResumeState === optionsResumeRMS[1].value) {
    yield put(closeRemoteSellingResumeModal())
    yield put(openSendingLinkModalRemoteSelling())
  } else if (remoteSellingResumeState === optionsResumeRMS[0].value) {
  }
}

export const onConfirmUsingRemoteSellingMode = function*(service) {
  const app = yield select(getCurrentApp)
  try {
    const remoteSellingState = getRemoteSellingState(app)

    if (!remoteSellingState) return null

    let isLinkModalOpen = true
    let isResendModalOpen = false

    if (remoteSellingState?.roomId) {
      isLinkModalOpen = false
      isResendModalOpen = true
    }

    yield put(
      setAppValue('remoteSelling', {
        ...remoteSellingState,
        isDataConfirmed: true,
        isLinkModalOpen,
        isResendModalOpen,
        resumeState: 'continue',
        isOpenNewRemoteSellingPreviewInfoModal: false,
      })
    )
  } catch (e) {
    console.error('onConfirmUsingRemoteSellingMode :', e.message)
  } finally {
    yield put(hideLoading())
  }
}

export const onClearEkycResult = function*(service, action) {
  let { insuredOrPayor } = action.payload

  if (!insuredOrPayor) {
    yield put(
      setAppValue('ekycInfo', {
        insured: {
          resendLink: false,
          isDataConfirmed: false,
        },
        payer: {
          resendLink: false,
          isDataConfirmed: false,
        },
        sendLink: {
          textInput: '',
          method: '',
        },
        isOnDevice: {
          value: null,
        },
      })
    )
  } else {
    yield put(setAppValue(`ekycInfo.${insuredOrPayor}`, { isDataConfirmed: false }))
  }

  const app = yield select(getCurrentApp)
  const attachments = _.get(app, '_attachments', {})

  yield put(
    setAppValue(
      '_attachments',
      omit(
        ['insured-face-recognition', 'owner-face-recognition', 'insured-face-image', 'owner-face-image'],
        attachments
      )
    )
  )
}

export const onClearRemoteSellingResult = function*(service, action) {
  try {
    yield put(clearEkycResults())
  } catch (e) {
    console.error('onClearRemoteSellingResult :', e)
  }
}

export const onEnterEkycF2f = function*(service, action) {
  let { insuredOrPayor } = action.payload
  const remoteSellingFields = REMOTE_SELLING.initialValue
  yield call(onClearEkycResult, service, action)
  yield call(setFaceImage, service, { image: null, insuredOrPayor })
  yield call(setFaceRecognitionImage, service, { image: null, insuredOrPayor })
  yield put(setAppValue(`ekycInfo.${insuredOrPayor}`, { isDataConfirmed: true }))
  yield put(setAppValue('remoteSelling', remoteSellingFields))
}

export const onInitRemoteSelling = function*(service, action) {
  yield put(setAppValue('selectedRemoteSelling', true))
  yield put(setAppValue('isRemoteSelling', false))
  yield put(setAppValue('remoteSelling', {}))
  yield put(setAppValue('ekycInfo', {}))
}

export const onInitOldRemoteSelling = function*(service, action) {
  yield put(setAppValue('selectedRemoteSelling', false))
  yield put(setAppValue('isRemoteSelling', true))
  yield put(setAppValue('remoteSelling', {}))
  yield put(setAppValue('ekycInfo', {}))
}

export const onReGenBI = function*(service, history, action) {
  let didHideModalLoading = false

  try {
    const {
      regenerateBi,
      setAttachment,
      getPolicyStatus,
      rollbackStatus,
      deleteUnsignDocument,
      regeneratePolicyBi,
    } = service
    const { appId, status } = action.payload
    const user = yield select(getIdentityUser)
    const app = yield select(getCurrentApp)
    const newPremiumCalData = _.get(app, ['newPremiumCalData'], {})
    const reValidateAgeData = _.get(app, ['reValidateAgeData'], {})
    const isSignedApplication = _.get(app, ['status'], {}) === 'signed'
    const totalPremium = _.get(newPremiumCalData, ['totalPremium'])
    const currentInsuredAgeFromEkycDob = _.get(reValidateAgeData, 'currentInsuredAgeFromEkycDob')
    const currentInsuredAge = _.get(reValidateAgeData, 'currentInsuredAge')

    if (totalPremium === undefined || totalPremium === null) {
      throw new Error('onReGenBI: missing newPremiumCalData.totalPremium')
    }

    const { response, timeout } = yield race({
      response: call(regenerateBi, user, appId, totalPremium),
      timeout: delay(REGEN_BI_TIMEOUT_MS),
    })

    if (timeout) {
      throw new Error('onReGenBI: regenerateBi timed out')
    }

    const regenerateBiStatus = _.get(response, 'status', 500)
    if (regenerateBiStatus === 200) {
      const isEkycDobDiffFromQqDob = _.get(reValidateAgeData, 'isEkycDobDiffFromQqDob')
      const ekycDob = _.get(reValidateAgeData, 'ekycDob')

      if (isEkycDobDiffFromQqDob && ekycDob) {
        yield put(setAppValue('quickQuote.insured.birthdate.value', ekycDob))
        yield put(setAppValue(...appDocsPut('quickQuote.biProps.birthdate', ekycDob)))
      }

      yield put(setAppValue('checkedPremiumCalStatus', 'Y'))
      yield put(saveAppData())
      const pdfBase64 = _.get(response, ['body', '0', 'base64Content'], '')

      const setAttachmentStartedAt = Date.now()
      let setAttachmentRace
      if (isSignedApplication) {
        const policyIdForAttachment = _.get(app, ['policyId'], {})
        const createPdfBlob = base64ToBlob(pdfBase64, 'application/pdf')
        setAttachmentRace = yield race({
          ok: call(setAttachment, { _id: `unsigned-doc_${policyIdForAttachment}_bi` }, 'doc', createPdfBlob, user),
          timeout: delay(REGEN_BI_TIMEOUT_MS),
        })
      } else {
        setAttachmentRace = yield race({
          ok: call(setAttachment, app, 'bi', pdfBase64, user),
          timeout: delay(REGEN_BI_TIMEOUT_MS),
        })
      }

      if (setAttachmentRace.timeout) {
        throw new Error('onReGenBI: setAttachment timed out')
      }

      yield put(setAppValue('quickQuote.coveragePlan.totalPremium.value', totalPremium))
      yield put(setAppValue(...appDocsPut('quickQuote.biProps.totalPremium', totalPremium)))
      yield put(
        setAppValue(
          'quickQuote.coveragePlan.selectedBasicPlan.premium',
          _.get(newPremiumCalData, ['basicPlan', 'basicPremium'])
        )
      )

      const saveRiders = []
      const ridersNeedSumAssured = ['PBPDD', 'PBSDD', 'PBPDDCI', 'PBSDDCI']
      newPremiumCalData.riders.map((rider) => {
        const riders = _.get(app, 'quickQuote.coveragePlan.riders')
        for (var riderPlanCode in riders) {
          if (riders.hasOwnProperty(riderPlanCode)) {
            const insuredRider = riders[riderPlanCode]
            const isSelected = _.get(insuredRider, 'isSelected', false)
            const isSelectable = _.get(insuredRider, 'isSelectable', false)
            if (isSelected && isSelectable) {
              const insuredRiderSelectedPlanCode = _.get(insuredRider, 'selectedPlan.planCode', '')
              const selectedPlanCode = isEmpty(insuredRiderSelectedPlanCode)
                ? riderPlanCode
                : insuredRiderSelectedPlanCode
              if (selectedPlanCode === rider.riderCode) {
                saveRiders.push(
                  put(setAppValue(`quickQuote.coveragePlan.riders.${riderPlanCode}.premium`, rider.riderPremium))
                )
                if (ridersNeedSumAssured.includes(rider.riderCode)) {
                  saveRiders.push(
                    put(
                      setAppValue(
                        `quickQuote.coveragePlan.riders.${riderPlanCode}.sumAssured`,
                        _.get(newPremiumCalData, ['basicPlan', 'basicAnnualPremium'])
                      )
                    )
                  )
                }
              }
            }
          }
        }
      })
      yield saveRiders
      yield put(setAppValue('checkedPremiumCalStatus', status))
      yield put(saveAppData())
      const policyId = _.get(app, 'policyId', false)

      if (isEkycDobDiffFromQqDob) {
        yield put(setAppValue('quickQuote.insured.age.value', currentInsuredAgeFromEkycDob))
        yield put(setAppValue('quickQuote.insured.age.unit', 'year'))
        yield put(setAppValue(...appDocsPut('quickQuote.biProps.insured.age.value', currentInsuredAgeFromEkycDob)))
      } else {
        yield put(setAppValue('quickQuote.insured.age.value', currentInsuredAge))
        yield put(setAppValue('quickQuote.insured.age.unit', 'year'))
        yield put(setAppValue(...appDocsPut('quickQuote.biProps.insured.age.value', currentInsuredAge)))
      }

      yield put(saveAppData())
      yield put.resolve(hideModalLoading())
      didHideModalLoading = true
      yield put.resolve(showAgeRevalidating())
      yield take('SAVE_NEW_BI')
      yield put.resolve(hideAgeRevalidating())

      if (status === 'CPQ') {
        if (isSignedApplication) {
          yield call(regeneratePolicyBi, {}, policyId, null, user)
          history.push('form/submit')
        } else {
          history.push('insured')
        }
      }
      if (status === 'CPE') {
      }
    } else {
      yield put(setAppValue('checkedPremiumCalStatus', 'NP'))
      yield put(setAppValue('isRegenBiFailed', true))
      yield put(saveAppData())
      yield put.resolve(hideModalLoading())
      didHideModalLoading = true
      yield put.resolve(hideAgeRevalidating())
      if (history.location.pathname === '/e-submission/list') {
        yield call(onNavigate, service, history)
      }
    }
  } catch (errors) {
    yield put(setAppValue('checkedPremiumCalStatus', 'NP'))
    yield put(setAppValue('isRegenBiFailed', true))
    yield put(saveAppData())
    yield put.resolve(hideModalLoading())
    didHideModalLoading = true
    yield put.resolve(hideAgeRevalidating())
    if (history.location.pathname === '/e-submission/list') {
      yield call(onNavigate, service, history)
    }
  } finally {
    if (!didHideModalLoading) {
      yield put.resolve(hideModalLoading())
      yield put.resolve(hideAgeRevalidating())
    }
  }
}

export const onNewReValidateAge = function*(service, history, action) {
  const { reValidateAge, checkPremiumCal } = service
  const { appId } = action.payload
  const app = yield select(getCurrentApp)

  try {
    const user = yield select(getIdentityUser)
    let ageChange = _.get(app, 'ageChange', false) ? 'Y' : 'N'
    let isSpecificAgeChanged = _.get(app, 'isSpecificAgeChanged', false)
    let checkedPremiumCalStatus = _.get(app, 'checkedPremiumCalStatus', '')

    const response = yield call(reValidateAge, user, appId)
    yield put(setAppValue('reValidateAgeData', response))
    yield put(saveAppData())

    ageChange = _.get(response, 'ageChange', false) ? 'Y' : 'N'
    isSpecificAgeChanged = _.get(response, ['isSpecificAgeChanged'], false)
    yield put(setAppValue('ageChange', ageChange))
    yield put(saveAppData())

    if (isSpecificAgeChanged) {
      checkedPremiumCalStatus = 'N'
      yield put(setAppValue('checkedPremiumCalStatus', checkedPremiumCalStatus))
      yield put(saveAppData())
    }

    if (ageChange === 'Y' && !isSpecificAgeChanged) {
      yield put.resolve(showModalLoading())
      const responseCheckPremiumCal = yield call(checkPremiumCal, user, appId)
      const responseCheckPremiumCalCode = _.get(responseCheckPremiumCal, ['code'], 999)
      const responseCheckPremiumCalErrors = _.get(responseCheckPremiumCal, ['errors'], [])
      if (responseCheckPremiumCalCode === 200) {
        if (responseCheckPremiumCalErrors.length === 0) {
          checkedPremiumCalStatus = 'Y'
          const responseCheckPremiumCalData = _.get(responseCheckPremiumCal, ['data'], 0)
          yield put(setAppValue('newPremiumCalData', responseCheckPremiumCalData))
          yield put(saveAppData())
          yield put({
            type: 'REGEN_BI',
            payload: {
              appId,
              status: history.location.pathname === '/e-submission/list' ? 'CPQ' : 'CPE',
            },
          })
        } else {
          checkedPremiumCalStatus = 'N'
        }
      } else if (responseCheckPremiumCalCode === 500) {
        const responseCheckPremiumCalErrorMessage = _.get(responseCheckPremiumCal, ['message'], '')
        if (
          responseCheckPremiumCalErrorMessage.includes('ไม่พบผลิตภัณฑ์') ||
          responseCheckPremiumCalErrorMessage.includes('no operator')
        ) {
          checkedPremiumCalStatus = 'NP'
        } else {
          checkedPremiumCalStatus = 'N'
        }
      } else {
        checkedPremiumCalStatus = 'NP'
      }
    } else if (ageChange === 'N') {
      const isEkycDobDiffFromQqDob = _.get(response, 'isEkycDobDiffFromQqDob')
      const ekycDob = _.get(response, 'ekycDob')
      if (isEkycDobDiffFromQqDob && ekycDob) {
        yield put(setAppValue('quickQuote.insured.birthdate.value', ekycDob))
        yield put(setAppValue(...appDocsPut('quickQuote.biProps.birthdate', ekycDob)))
        yield put(saveAppData())
      }
    }

    if (checkedPremiumCalStatus !== 'Y') {
      yield put.resolve(hideModalLoading())
      yield put(setAppValue('checkedPremiumCalStatus', checkedPremiumCalStatus))
      yield put(saveAppData())
    }

    if (checkedPremiumCalStatus !== 'N') {
      if (
        (ageChange === 'N' || checkedPremiumCalStatus === 'NP') &&
        history.location.pathname === '/e-submission/list'
      ) {
        yield call(onNavigate, service, history)
      }
    }
  } catch (errors) {
    yield put(setAppValue('checkedPremiumCalStatus', 'NP'))
    yield put(saveAppData())
    yield put.resolve(hideModalLoading())
    const message = errors.message
    const notificationMessage = getToggles().ENABLE_RED_TRUCK_ERROR_MSG
      ? { type: 'GenericError', message: message }
      : { type: 'GenericError' }
    yield put(setNotificationMessage(notificationMessage))
  }
}

export const onNavigate = function*(service, history) {
  const app = yield select(getCurrentApp)
  const status = _.get(app, 'status', null)
  const remoteSelling = _.get(app, ['remoteSelling'], null)
  const selectedRemoteSelling = _.get(app, ['selectedRemoteSelling'], null)
  const isRemoteSelling = _.get(app, ['isRemoteSelling'], null)

  if (status === APP_STATUS.SIGNED && remoteSelling && !remoteSelling.videoConsented && remoteSelling.isDataConfirmed) {
    history.push(ROUTE_PATH.REMOTE_SELLING.VIDEO_CONSENT)
  } else if (status === APP_STATUS.SUBMITTED || status === APP_STATUS.SIGNED) {
    if (history.location.pathname === '/e-submission/list') {
      history.push('form/submit')
    } else {
      history.push('/e-submission/form/submit')
    }
  } else if (
    (isNil(status) || status === APP_STATUS.DRAFTED) &&
    remoteSelling &&
    remoteSelling.isDataConfirmed &&
    selectedRemoteSelling &&
    getToggles().ENABLE_APPMAN_REMOTE_SELLING
  ) {
    if (history.location.pathname === '/e-submission/list') {
      history.push('form/ekyc')
    } else {
      history.push('/e-submission/form/ekyc')
    }
  } else if (
    (isNil(status) || status === APP_STATUS.DRAFTED) &&
    isRemoteSelling &&
    !selectedRemoteSelling &&
    getToggles().ENABLE_APPMAN_REMOTE_SELLING
  ) {
    if (history.location.pathname === '/e-submission/list') {
      history.push('form/ekyc')
    } else {
      history.push('/e-submission/form/ekyc')
    }
  } else if (isNil(status) || status === APP_STATUS.DRAFTED) {
    if (history.location.pathname === '/e-submission/list') {
      history.push('form')
    } else {
      history.push('/e-submission/form/signature')
    }
  }
}

export const oniProLoadApp = function*(service, history, action) {
  let { appId, status } = action.payload
  yield put({
    type: 'CHANGE_CURRENT_APP',
    appId,
  })
  while (true) {
    const app = yield select(getCurrentApp)
    if (app) {
      yield put({ type: 'CHANGE_CURRENT_APPLICATION_SUCCESS', appId })
      break
    }
    yield delay(500)
  }
}

export const onChangeCurrentAppSuccess = function*(service, history, action) {
  const { appId } = action
  yield put({
    type: 'HANDLE_CLICKING_APPLICATION_CARD',
    appId,
  })
}

export default function*(service, history) {
  const SAVE_ACTIONS = getToggles().ENABLE_OPTIMIZE_SAVING_REQUEST
    ? ['SAVE_APP_DATA']
    : ['SET_APP_VALUE', 'UNSET_APP_VALUE', 'REMOVE_APP_MEMBER']

  let replicationTask = null
  let checkSubmittingTask = null

  yield [
    takeLatest(SAVE_ACTIONS, onDebounceSave, service),
    takeEvery('SET_APP_VALUE', onDebounceSave, service),
    takeLatest(['UNSET_APP_VALUE', 'REMOVE_APP_MEMBER'], onDebounceSave, service),
    takeLatest('ERROR_OTP_ATTACHMENTS', onErrorOtpAttachments, service, history),
    takeLatest('FETCH_ALL_APP', onList, service),
    takeLatest('FETCH_CURRENT_APP', onLoad, service),
    takeLatest('SEND_APPLICATION', onSend, service),
    takeLatest('SUBMIT_APPLICATION', onSubmit, service),
    takeEvery('CREATE_NEW_APP', onCreate, service),
    takeEvery('CHANGE_CURRENT_APP', onUpdate, service),
    takeEvery('HANDLE_CLICKING_APPLICATION_CARD', onApplicationCardClicked, service, history),
    takeEvery('CREATE_CASE_EKYC', onSendLinkEkyc, service),
    takeEvery('CREATE_CASE_EKYC_DEVICE', onCreateEkycByDevice, service),
    takeEvery('REUSE_CASE_EKYC', onReuseEkyc, service),
    takeEvery('VERIFY_EKYC', onVerifyEkyc, service),
    takeEvery('GET_ATTACHMENT', onGetAttachment, service),
    takeLatest('SUBSCRIBE_REPLICATION_STATUS', function*() {
      replicationTask = yield spawn(onSync, service)
      checkSubmittingTask = yield spawn(onCheckSubmitting, service)
    }),
    takeLatest('UNSUBSCRIBE_REPLICATION_STATUS', function*() {
      if (replicationTask) {
        yield cancel(replicationTask)
        replicationTask = null
      }
      if (checkSubmittingTask) {
        yield cancel(checkSubmittingTask)
        checkSubmittingTask = null
      }
    }),
    takeEvery(REMOTE_SELLING_CREATE_NEW_LINK, onCreateNewLinkRemoteSelling, service),
    takeEvery(REMOTE_SELLING_RESET_DATA, onResetRemoteSelling, service),
    takeEvery(REMOTE_SELLING_USING_EXISTS_LINK, onGetAttachment, service),
    takeEvery(REMOTE_SELLING_SUBMIT_RESUME_MODAL, onSubmitRemoteSellingResumeModal, service),
    takeEvery(CONFIRM_REMOTE_SELLING, onConfirmUsingRemoteSellingMode, service),
    takeEvery(CLEAR_REMOTE_SELLING_RESULT, onClearRemoteSellingResult, service),
    takeEvery(CLEAR_EKYC_RESULT, onClearEkycResult, service),
    takeEvery(ENTER_EKYC_F2F, onEnterEkycF2f, service),
    takeEvery('INIT_REMOTE_SELLING', onInitRemoteSelling, service),
    takeEvery('INIT_OLD_REMOTE_SELLING', onInitOldRemoteSelling, service),
    takeEvery('RE_VALIDATE_AGE', onNewReValidateAge, service, history),
    takeEvery('REGEN_BI', onReGenBI, service, history),
    takeEvery('IPRO_LOAD_APP', oniProLoadApp, service, history),
    takeEvery('CHANGE_CURRENT_APPLICATION_SUCCESS', onChangeCurrentAppSuccess, service, history),
  ]
}
